import { formatIDR } from "@marketplace/shared/utils";
import { Button, Text, UnstyledButton } from "@marketplace/ui";
import { type DataTableColumn } from "@marketplace/ui/data-table";
import { useSetAtom } from "jotai";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  MdClose,
  MdOutlineDelete,
  MdOutlineTextSnippet,
  MdSwapHoriz
} from "react-icons/md";

import { usePermissions } from "@/hooks";
import { formatWithOffset } from "@/utils";

import { claimDeleteModalAtom } from "../atoms/claim-delete-modal-atom";
import { claimHistoryReviewModalAtom } from "../atoms/claim-history-review-modal-atom";
import { claimIntegrationStatusModalAtom } from "../atoms/claim-integration-status-modal-atom";
import { ClaimResubmitButton } from "../components/claim-resubmit-button";
import { ClaimStatusPill } from "../components/claim-status-pill";
import type { ClaimHistoryListItem } from "../dtos/get-claim-history-list-response-dto";

const SELLER_DELETABLE_STATUSES = ["review", "reject", "cancelled"];
const SUPERADMIN_DELETABLE_STATUSES = ["review", "reject", "cancelled"];

export const useClaimHistoryListTableColumns = (page: number) => {
  const { t } = useTranslation("order", {
    keyPrefix: "claimHistoryPage.table.columns"
  });
  const { t: tStatus } = useTranslation("order", {
    keyPrefix: "claimHistoryPage.statusTabs"
  });
  const setReviewModal = useSetAtom(claimHistoryReviewModalAtom);
  const setClaimIntegrationStatusModal = useSetAtom(
    claimIntegrationStatusModalAtom
  );
  const setClaimDeleteModal = useSetAtom(claimDeleteModalAtom);
  const { isSuperAdmin, isSeller, isSellerExternal } = usePermissions();

  const columns = useMemo<DataTableColumn<ClaimHistoryListItem>[]>(
    () => [
      {
        accessor: "no",
        title: t("no"),
        render: (_: ClaimHistoryListItem, index: number) =>
          (page - 1) * 10 + index + 1
      },
      {
        accessor: "public_id",
        title: t("claimId")
      },
      {
        accessor: "invoice_number",
        title: t("sellerInvoiceNumber"),
        render: (record) => record.invoice_number || "-"
      },
      {
        accessor: "lampu_invoice_number",
        title: t("lampuInvoiceNumber"),
        render: (record) => record.lampu_invoice_number || "-"
      },
      ...(isSuperAdmin
        ? [
            {
              accessor: "orders",
              title: t("seller"),
              render: (record: ClaimHistoryListItem) =>
                record.orders?.[0]?.seller_company_name || "-"
            }
          ]
        : []),
      {
        accessor: "invoice_amount",
        title: t("amount"),
        render: (record) => formatIDR(record.invoice_amount)
      },
      {
        accessor: "created_at",
        title: t("submittedAt"),
        render: (record) => formatWithOffset(record.created_at, "dd MMM yyyy")
      },
      {
        accessor: "current_status",
        title: t("claimStatus"),
        render: (record) => (
          <ClaimStatusPill
            status={record.current_status}
            label={record.status_label}
          />
        )
      },
      {
        accessor: "actions",
        title: t("action"),
        render: (record) => {
          const isSellerDeletable = SELLER_DELETABLE_STATUSES.includes(
            record.current_status
          );
          const isSuperAdminDeletable = SUPERADMIN_DELETABLE_STATUSES.includes(
            record.current_status
          );
          const canDelete =
            (isSeller && isSellerDeletable) ||
            (isSuperAdmin && isSuperAdminDeletable);
          const isSellerCancelled =
            isSeller && record.current_status === "reject";

          return (
            <div className="flex gap-x-2">
              {isSuperAdmin && record.current_status === "review" && (
                <Button
                  variant="primary"
                  size="sm"
                  rightSection={<MdOutlineTextSnippet size={14} />}
                  onClick={() =>
                    setReviewModal({
                      visible: true,
                      publicId: record.public_id
                    })
                  }
                >
                  {t("review")}
                </Button>
              )}
              {isSeller &&
                (record.current_status === "reject" ||
                  record.current_status === "cancelled") && (
                  <ClaimResubmitButton publicId={record.public_id} />
                )}
              <Button
                variant="secondary"
                size="sm"
                rightSection={<MdOutlineTextSnippet size={14} />}
                onClick={() =>
                  setReviewModal({
                    visible: true,
                    publicId: record.public_id,
                    isViewDetail: true
                  })
                }
              >
                {t("seeDetail")}
              </Button>
              {record.current_status !== "review" && !isSellerExternal && (
                <UnstyledButton
                  className="rounded bg-fjord-grey-500 text-fjord-grey-100 px-[10px] flex items-center gap-x-[6px] hover:bg-gray-600"
                  onClick={() =>
                    setClaimIntegrationStatusModal({
                      visible: true,
                      publicId: record.public_id
                    })
                  }
                >
                  <Text size="s">{t("integration")}</Text>
                  <MdSwapHoriz size={14} />
                </UnstyledButton>
              )}
              {canDelete && (
                <Button
                  variant="secondary"
                  size="sm"
                  radius="md"
                  aria-label={t("deleteAria")}
                  onClick={() =>
                    setClaimDeleteModal({
                      visible: true,
                      claim: record,
                      kind: isSellerCancelled ? "cancel" : "delete"
                    })
                  }
                >
                  {isSellerCancelled ? (
                    <MdClose size={14} />
                  ) : (
                    <MdOutlineDelete size={14} />
                  )}
                </Button>
              )}
            </div>
          );
        }
      }
    ],
    [
      page,
      t,
      tStatus,
      setReviewModal,
      setClaimIntegrationStatusModal,
      setClaimDeleteModal,
      isSuperAdmin,
      isSeller,
      isSellerExternal
    ]
  );

  return { columns };
};
